import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Map, Marker, useMap, useMapsLibrary } from '@vis.gl/react-google-maps';
import { MapPinIcon } from '@heroicons/react/24/solid';
import StringField from '../../forms/_fields_v2/StringField';
import placeToComponents from '../../utils/placeToComponents';

LocationSearchField.propTypes = {
  address: PropTypes.string,
  disabled: PropTypes.bool,
  placeholder: PropTypes.string,
  displayMap: PropTypes.bool,
  onChange: PropTypes.func.isRequired
};

const DEFAULT_CENTER = {
  lat: 37.923483,
  lng: -99.650695
};

const DEFAULT_ZOOM = 3;

/**
 * Moves the map to the selected place, preferring the viewport supplied by the
 * Places API and falling back to centering on the location itself.
 *
 * @param {object} props - Component props.
 * @param {object} props.place - The selected Place, or null if nothing is selected.
 * @returns {null} Renders nothing; it only drives the map camera.
 */
function MapCamera(props) {
  const { place } = props;
  const map = useMap();

  useEffect(() => {
    if (!map || !place) {
      return;
    }

    if (place.viewport) {
      map.fitBounds(place.viewport);
    } else if (place.location) {
      map.setCenter(place.location);
      map.setZoom(17); // Why 17? Because it looks good.
    }
  }, [map, place]);

  return null;
}

MapCamera.propTypes = {
  place: PropTypes.object
};

export default function LocationSearchField(props) {
  const {
    disabled,
    placeholder = "Enter a name or address",
    onChange,
    displayMap = true,
  } = props;

  const [inputValue, setInputValue] = useState(props.address || '');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [selectedPlace, setSelectedPlace] = useState(null);

  const places = useMapsLibrary('places');
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  // Debounce the query so we aren't issuing a request on every keystroke
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(inputValue.trim());
    }, 400);
    return () => clearTimeout(timer);
  }, [inputValue]);

  useEffect(() => {
    if (!places || !hasInteracted || debouncedQuery.length <= 1) {
      setSuggestions([]);
      return;
    }

    let cancelled = false;

    async function fetchSuggestions() {
      try {
        const { suggestions } = await places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
          input: debouncedQuery
        });

        if (!cancelled) {
          setSuggestions(suggestions || []);
          setShowDropdown(true);
        }
      } catch (e) {
        console.error('Places autocomplete error', e);
        if (!cancelled) setSuggestions([]);
      }
    }

    fetchSuggestions();

    return function() {
      cancelled = true;
    };
  }, [places, debouncedQuery, hasInteracted]);

  // Close the dropdown when clicking outside the field
  useEffect(() => {
    function handleClickOutside(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  function handleKeyDown(e) {
    if (e.key === 'Escape') {
      setShowDropdown(false);
      inputRef.current?.blur();
    }
  }

  /**
   * Handles selection of a place from the suggestion list, resolving it to the
   * address components the consuming form expects.
   *
   * @param {object} suggestion - The selected autocomplete suggestion.
   */
  async function onSelectSuggestion(suggestion) {
    const placePrediction = suggestion.placePrediction;
    const place = placePrediction.toPlace();

    try {
      await place.fetchFields({
        fields: [
          'id',
          'displayName',
          'formattedAddress',
          'addressComponents',
          'location',
          'viewport'
        ]
      });
    } catch (e) {
      console.error('fetchFields error', e);
      return;
    }

    setInputValue(placePrediction.text?.toString() || place.formattedAddress || '');
    setShowDropdown(false);
    setSelectedPlace(place);

    onChange({
      address: place.formattedAddress,
      latitude: place.location?.lat(),
      longitude: place.location?.lng()
    }, place, placeToComponents(place));
  }

  return (
    <div ref={containerRef} className="relative" onKeyDown={handleKeyDown}>
      <StringField
        {...props}
        onChange={(value) => {
          setInputValue(value);
          setHasInteracted(true);
        }}
        inputRef={inputRef}
        value={inputValue}
        placeholder={placeholder}
        disabled={disabled}
      />
      {showDropdown && suggestions.length > 0 && (
        <div
          role="listbox"
          aria-label="Search results"
          className="absolute left-0 right-0 z-50 mt-1 bg-white border border-cs-gray-200 rounded-xl shadow-md overflow-hidden"
        >
          <div className="overflow-y-auto max-h-[280px]">
            {suggestions.map((suggestion, index) => (
              <div
                key={index}
                role="option"
                aria-selected="false"
                tabIndex={-1}
                className={classNames(
                  'group px-4 py-3 cursor-pointer flex items-start gap-3',
                  'hover:bg-cs-orange-50 active:bg-cs-orange-100'
                )}
                onClick={() => onSelectSuggestion(suggestion)}
              >
                <MapPinIcon className="size-5 flex-shrink-0 mt-0.5 text-cs-gray-400 group-hover:text-cs-orange-400"/>
                <span className="text-sm underline leading-snug text-cs-gray-900 group-hover:text-cs-orange-600">
                  {suggestion.placePrediction?.text?.toString() || ''}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className={`${displayMap ? 'block' : 'hidden'} w-full mt-6`} style={{ height: '350px' }}>
        {displayMap && (
          <Map
            className="w-full h-full"
            defaultCenter={DEFAULT_CENTER}
            defaultZoom={DEFAULT_ZOOM}
          >
            <MapCamera place={selectedPlace}/>
            {selectedPlace?.location && (
              <Marker position={selectedPlace.location}/>
            )}
          </Map>
        )}
      </div>
    </div>
  );
};
